import React, { useEffect } from 'react';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import * as FileSaver from 'file-saver';
import { PDFDocument, degrees } from 'pdf-lib';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useComponentState from '../../utils/useComponentState';
import connectPrinter from '../../utils/connectPrinter';
import { getCarrierBook, getQrPdf } from '../../api/Vouchers';
import {
  savePdfFile,
  formatAmount,
} from '../../screens/ViewVouchers/ViewVouchersUtils';
import {
  BOOK_VARIANTS,
  QR_METHODS,
  LABEL_FIELDS,
  MAX_LABEL_FIELDS,
  PRINT_OPTIONS_INITIAL_STATE,
} from '../../constants/viewVouchers';

import {
  faBook,
  faQrcode,
  faEnvelope,
  faBoxOpen,
  faPrint,
  faCheck,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';

import styles from './PrintOptionsModal.module.scss';

const valueForLabelField = (key, { ownerName, sender, amount, id, purchaseId }) => {
  switch (key) {
    case 'receiverName':
      return ownerName;
    case 'senderName':
      return sender;
    case 'amount':
      return `LKR ${formatAmount(amount)}`;
    case 'purchaseId':
      return purchaseId;
    case 'voucherId':
      return id;
    default:
      return '';
  }
};

const computeLabelFieldValues = (fields, entry) => {
  let topField = '';
  let bottomField = null;

  if (fields.length > 1) {
    if (fields.includes('voucherId')) {
      const otherKey = fields.find((key) => key !== 'voucherId');
      topField = valueForLabelField(otherKey, entry);
      bottomField = entry.id;
    } else {
      topField = valueForLabelField(fields[0], entry);
      bottomField = valueForLabelField(fields[1], entry);
    }
  } else {
    topField = valueForLabelField(fields[0], entry);
  }

  return { topField, bottomField };
};

const buildLabelZpl = (labelsToPrint, fields) => {
  let zpl = '';

  labelsToPrint.forEach((entry) => {
    const { topField, bottomField } = computeLabelFieldValues(fields, entry);

    const fontsize = topField.length <= 20 ? 45 : topField.length <= 30 ? 40 : 25;
    const topFieldYOffset = fields.length > 1 ? 40 : 50;
    const bottomFieldYOffset = fields.length > 1 ? 120 : 130;
    const footerYOffset = 180;

    zpl += `^XA
^MD22
^PW799
^LL300
^FO0,${topFieldYOffset}
^FB799,1,0,C,0
^CF0,${fontsize},${fontsize}
^C^FH\^FD${topField || ''}^FS

${
      bottomField
        ? `^FO0,${bottomFieldYOffset}
^FB799,1,0,C,0
^A0N,25,25
^C^FH\^FD${bottomField}^FS`
        : ''
    }

^FO0,${footerYOffset}
^FB799,1,0,C,0
^A0N,18,18
^C^FH\^FDOne voucher, 200+ merchants | thyaga.lk^FS
^XZ`;
  });

  return zpl;
};

const rotatePdfToPortrait = async (base64Pdf) => {
  const pdfBytes = Buffer.from(base64Pdf, 'base64');
  const pdfDoc = await PDFDocument.load(pdfBytes);

  pdfDoc.getPages().forEach((page) => {
    const { width, height } = page.getSize();
    if (width > height) {
      page.setRotation(degrees(90));
    }
  });

  return pdfDoc.save();
};

const PrintOptionsModal = (props) => {
  const { show, handleClose, voucherIds = [], labelsToPrint = [], token } = props;

  const [
    {
      bookEnabled,
      bookVariant,
      qrEnabled,
      qrMethod,
      envelopeEnabled,
      envelopeFields,
      packageEnabled,
      packageFields,
      printing,
    },
    setState,
  ] = useComponentState(PRINT_OPTIONS_INITIAL_STATE);

  const handleModalClose = () => {
    setState(PRINT_OPTIONS_INITIAL_STATE);
    handleClose();
  };

  useEffect(() => {
    if (!show) return undefined;

    const isOutside = (e) => !e.target.closest('.modal-content');

    // Swallow mouseup/click in the capture phase so an outside press can't
    // also activate whatever page element happens to sit under the cursor.
    const swallowIfOutside = (e) => {
      if (isOutside(e)) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    const handleMouseDown = (e) => {
      if (isOutside(e)) {
        e.stopPropagation();
        e.preventDefault();
        handleModalClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mouseup', swallowIfOutside, true);
    document.addEventListener('click', swallowIfOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('mouseup', swallowIfOutside, true);
      document.removeEventListener('click', swallowIfOutside, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const toggleLabelField = (fieldsKey, key) => {
    setState((prev) => {
      const current = prev[fieldsKey];
      let updated;
      if (current.includes(key)) {
        updated = current.filter((item) => item !== key);
      } else if (current.length >= MAX_LABEL_FIELDS) {
        return prev;
      } else {
        updated = [...current, key];
      }
      return { ...prev, [fieldsKey]: updated };
    });
  };

  const printBook = async () => {
    const variant = BOOK_VARIANTS.find((item) => item.value === bookVariant);
    const request = voucherIds.map((id) => ({
      voucherId: id,
      amount: true,
      to: variant.to,
      from: variant.from,
      body: variant.body,
    }));
    const carrierBookResponse = await getCarrierBook(request, token);
    const rotatedPdfBytes = await rotatePdfToPortrait(carrierBookResponse);
    const blob = new Blob([rotatedPdfBytes], { type: 'application/pdf' });
    FileSaver.saveAs(blob, `Print_Book_${variant.value.replace(/ /g, '_')}`);
  };

  const printQrZebra = async () => {
    const SHEET_SIZE = 3;
    let index = 0;
    let device = null;

    try {
      device = await connectPrinter();
      const encoder = new TextEncoder();

      while (index < voucherIds.length) {
        let zpl = '^XA\n^MD22\n^PW799\n^LL799\n';

        for (let pos = 0; pos < SHEET_SIZE && index < voucherIds.length; pos++) {
          const id = voucherIds[index];
          const positionX = pos === 0 ? 38 : pos === 1 ? 302 : 566;
          const textX = pos === 0 ? 70 : pos === 1 ? 334 : 600;

          zpl += `^FO${positionX},6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS\n`;
          zpl += `^FO${textX},220^A0N,28,28^FD${id}^FS\n`;

          index++;
        }

        zpl += '^XZ\n';
        await device.transferOut(1, encoder.encode(zpl));
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    } finally {
      if (device) {
        try {
          await device.releaseInterface(0);
        } catch (e) {}
        try {
          await device.close();
        } catch (e) {}
      }
    }
  };

  const downloadQrPdf = async () => {
    const request = { voucherIds };
    const rawPdfResponse = await getQrPdf(request, token);
    savePdfFile(rawPdfResponse, 'QR_Code');
  };

  const printLabelZebra = async (fields) => {
    const zpl = buildLabelZpl(labelsToPrint, fields);
    const device = await connectPrinter();
    const encoder = new TextEncoder();
    await device.transferOut(1, encoder.encode(zpl));
  };

  const handlePrintSelected = async () => {
    setState({ printing: true });
    const failedSections = [];

    if (bookEnabled) {
      try {
        await printBook();
      } catch (error) {
        failedSections.push('Print book');
      }
    }

    if (qrEnabled) {
      try {
        if (qrMethod === 'zebra') {
          await printQrZebra();
        } else {
          await downloadQrPdf();
        }
      } catch (error) {
        failedSections.push('QR codes');
      }
    }

    if (envelopeEnabled && envelopeFields.length > 0) {
      try {
        await printLabelZebra(envelopeFields);
      } catch (error) {
        failedSections.push('Envelope / box label');
      }
    }

    if (packageEnabled && packageFields.length > 0) {
      try {
        await printLabelZebra(packageFields);
      } catch (error) {
        failedSections.push('Package label');
      }
    }

    setState({ printing: false });

    if (failedSections.length === 0) {
      toast.success('Selected outputs processed successfully');
      handleModalClose();
    } else {
      toast.error(`Failed to process: ${failedSections.join(', ')}`);
    }
  };

  const outputsSelectedCount = [bookEnabled, qrEnabled, envelopeEnabled, packageEnabled].filter(
    Boolean
  ).length;

  const envelopeInvalid = envelopeEnabled && envelopeFields.length === 0;
  const packageInvalid = packageEnabled && packageFields.length === 0;
  const canPrint =
    outputsSelectedCount > 0 && !envelopeInvalid && !packageInvalid && !printing;

  const renderFieldChips = (fieldsKey, fields, sectionClass) => (
    <div className={styles.chipRow}>
      {LABEL_FIELDS.map(({ key, label }) => {
        const selected = fields.includes(key);
        const disabled = !selected && fields.length >= MAX_LABEL_FIELDS;
        return (
          <button
            type='button'
            key={key}
            className={`${styles.chip} ${sectionClass} ${selected ? styles.chipSelected : ''}`}
            disabled={disabled}
            onClick={() => toggleLabelField(fieldsKey, key)}
          >
            <FontAwesomeIcon icon={selected ? faCheck : faPlus} className={styles.chipIcon} />
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <Modal show={show} onHide={handleModalClose} centered size='lg' className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.titleRow}>
          <span>Print options</span>
          <span className={styles.subtitle}>
            {voucherIds.length} voucher{voucherIds.length !== 1 ? 's' : ''} selected
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Form.Check
              type='checkbox'
              checked={bookEnabled}
              onChange={(e) => setState({ bookEnabled: e.target.checked })}
            />
            <FontAwesomeIcon icon={faBook} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print book</p>
              <p className={styles.sectionSubtitle}>Choose what shows on the voucher</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!bookEnabled}
              value={bookVariant}
              onChange={(e) => setState({ bookVariant: e.target.value })}
            >
              {BOOK_VARIANTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Form.Check
              type='checkbox'
              checked={qrEnabled}
              onChange={(e) => setState({ qrEnabled: e.target.checked })}
            />
            <FontAwesomeIcon icon={faQrcode} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>QR codes</p>
              <p className={styles.sectionSubtitle}>Pick output method</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!qrEnabled}
              value={qrMethod}
              onChange={(e) => setState({ qrMethod: e.target.value })}
            >
              {QR_METHODS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={`${styles.section} ${styles.envelopeSection}`}>
          <div className={`${styles.sectionHeader} ${styles.envelopeHeader}`}>
            <Form.Check
              type='checkbox'
              checked={envelopeEnabled}
              onChange={(e) => setState({ envelopeEnabled: e.target.checked })}
            />
            <FontAwesomeIcon icon={faEnvelope} className={styles.sectionIcon} />
            <p className={styles.sectionTitle}>Print label on envelope / box</p>
          </div>
          {renderFieldChips('envelopeFields', envelopeFields, styles.envelopeChip)}
          {envelopeInvalid && (
            <small className='text-danger'>Choose at least one field</small>
          )}
        </div>

        <div className={`${styles.section} ${styles.packageSection}`}>
          <div className={`${styles.sectionHeader} ${styles.packageHeader}`}>
            <Form.Check
              type='checkbox'
              checked={packageEnabled}
              onChange={(e) => setState({ packageEnabled: e.target.checked })}
            />
            <FontAwesomeIcon icon={faBoxOpen} className={styles.sectionIcon} />
            <p className={styles.sectionTitle}>Print label on package</p>
          </div>
          {renderFieldChips('packageFields', packageFields, styles.packageChip)}
          {packageInvalid && (
            <small className='text-danger'>Choose at least one field</small>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <span className={styles.outputsSelected}>
          {outputsSelectedCount} output{outputsSelectedCount !== 1 ? 's' : ''} selected
        </span>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handlePrintSelected} disabled={!canPrint}>
            {printing ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faPrint} />
                Print selected
              </span>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PrintOptionsModal;
